import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AppPaths from '../../../../AppPaths';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { stripeCheckout } from '../../../../lib/authentication';
import {
  StripeCheckoutAccountExistsError,
  ThreeDSecureRequiredError,
  UserFacingError,
  logInternalError,
} from '../../../../lib/errors';
import * as Sentry from '@sentry/react';
import useCheckoutPaths from '../../../../hooks/useCheckoutPaths';
import useTenancy from '../../../../hooks/useTenancy';
import * as types from '../../../../types';
import { StripeAddressValue, StripePaymentMethodDetails } from '../../../../components/StripeCheckout';
import { getFiGrowthBook } from '../../../../lib/growthbook';
import { parseStripeAddressName } from '../../../../lib/nameParser';
import { sessionActions } from '../../../../reducers/session';

interface SubmitCheckoutParams {
  tokenId: string;
  email: string;
  address: StripeAddressValue;
  shippingCode: types.ShippingCode;
  paymentMethodDetails: StripePaymentMethodDetails;
}

export default function useStripeCheckout() {
  const { t } = useTranslation();
  const { checkoutActions } = useContext(CheckoutContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const checkoutPaths = useCheckoutPaths();
  const tenancy = useTenancy();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [threeDSecureActionToken, setThreeDSecureActionToken] = useState<string | null>(null);
  const [threeDSecurePending, setThreeDSecurePending] = useState(false);
  const [paramsForResubmission, setParamsForResubmission] = useState<SubmitCheckoutParams | null>(null);

  const handleSuccess = useCallback(
    (session: types.Session, params: SubmitCheckoutParams) => {
      const { address, shippingCode, paymentMethodDetails } = params;
      const { firstName, lastName } = parseStripeAddressName(address);
      const shippingAddress: types.gqlTypes.AddressInput = {
        line1: address.address.line1,
        line2: address.address.line2 ?? '',
        city: address.address.city,
        state: address.address.state,
        zip: address.address.postal_code,
        country: address.address.country,
        phone: address.phone,
      };

      const paymentInfo: types.PaymentInfo =
        paymentMethodDetails.type === 'us_bank_account'
          ? {
              __typename: 'BankAccountInfo',
              bankName: paymentMethodDetails.bankName,
              last4: paymentMethodDetails.last4,
              accountType: paymentMethodDetails.accountType,
            }
          : {
              __typename: 'ObfuscatedCardInfo',
              type: paymentMethodDetails.brand,
              lastFour: parseInt(paymentMethodDetails.last4, 10),
              month: paymentMethodDetails.expMonth,
              year: paymentMethodDetails.expYear,
            };

      dispatch(sessionActions.createSession({ session }));
      dispatch(checkoutActions.setCustomerName({ firstName, lastName }));
      dispatch(checkoutActions.setShippingAddress({ shippingAddress }));
      dispatch(checkoutActions.setShippingCode({ shippingCode }));
      dispatch(
        checkoutActions.setBillingInfo({
          billingInfo: {
            firstName,
            lastName,
            address: {
              line1: shippingAddress.line1,
              line2: shippingAddress.line2 ?? '',
              city: shippingAddress.city,
              state: shippingAddress.state,
              zip: shippingAddress.zip,
              country: shippingAddress.country,
              phone: shippingAddress.phone,
            },
            paymentInfo,
          },
        }),
      );

      history.push(checkoutPaths.Review);
    },
    [dispatch, checkoutActions, history, checkoutPaths.Review],
  );

  const executeCheckout = useCallback(
    async (params: SubmitCheckoutParams, threeDSecureActionResultToken?: string) => {
      const { tokenId, email, address } = params;
      const { firstName, lastName } = parseStripeAddressName(address);

      const session = await stripeCheckout({
        confirmationToken: tokenId,
        email,
        firstName,
        lastName,
        address1: address.address.line1,
        address2: address.address.line2 ?? undefined,
        city: address.address.city,
        state: address.address.state,
        phone: address.phone ?? '',
        zip: address.address.postal_code,
        country: address.address.country,
        tenancy,
        threeDSecureActionResultToken,
      });

      return session;
    },
    [tenancy],
  );

  const submitCheckout = useCallback(
    async (params: SubmitCheckoutParams) => {
      setSubmitting(true);
      setError(undefined);

      try {
        const session = await executeCheckout(params);
        handleSuccess(session, params);
      } catch (err) {
        if (err instanceof StripeCheckoutAccountExistsError) {
          setError(err.message);
          history.push({
            pathname: AppPaths.Login,
            search: `?returnTo=${AppPaths.Bag}`,
            state: { stripeCheckoutError: true },
          });
        } else if (err instanceof ThreeDSecureRequiredError) {
          setThreeDSecureActionToken(err.actionTokenId);
          setThreeDSecurePending(true);
          setParamsForResubmission(params);
        } else if (err instanceof UserFacingError) {
          setError(err.message);
        } else {
          logInternalError(err, {
            fingerprint: ['{{ default }}', err instanceof Error ? err.message : 'unknown'],
            tags: { checkoutStep: 'stripeCheckout' },
          });
          setError(t('something_went_wrong'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [t, executeCheckout, handleSuccess, history],
  );

  const onThreeDSecureToken = useCallback(
    async (token: { id: string }) => {
      if (getFiGrowthBook().getFeatureValue('alert-on-stripe-elements-3ds', true)) {
        Sentry.captureMessage('3DS challenge succeeded (billing step)', {
          level: 'info',
          tags: {
            checkoutStep: 'stripeCheckout3DSecure',
            threeDSecureResult: 'success',
          },
        });
      }

      if (!paramsForResubmission) {
        setError(t('something_went_wrong'));
        setThreeDSecurePending(false);
        return;
      }

      setSubmitting(true);
      setError(undefined);
      setThreeDSecureActionToken(null);

      try {
        const session = await executeCheckout(paramsForResubmission, token.id);
        handleSuccess(session, paramsForResubmission);
      } catch (err) {
        if (err instanceof UserFacingError) {
          setError(err.message);
        } else {
          logInternalError(err, {
            fingerprint: ['{{ default }}', err instanceof Error ? err.message : 'unknown'],
            tags: { checkoutStep: 'stripeCheckout3DSResubmit' },
          });
          setError(t('something_went_wrong'));
        }
      } finally {
        setSubmitting(false);
        setThreeDSecurePending(false);
        setParamsForResubmission(null);
      }
    },
    [t, paramsForResubmission, executeCheckout, handleSuccess],
  );

  const onThreeDSecureError = useCallback((e: { message: string }) => {
    const isVendorLoadError = (e as any).code === '3ds-vendor-load-error';
    if (getFiGrowthBook().getFeatureValue('alert-on-stripe-elements-3ds', true)) {
      logInternalError(e instanceof Error ? e : new Error(e.message), {
        fingerprint: ['{{ default }}', e.message],
        tags: {
          checkoutStep: 'stripeCheckout3DSecure',
          threeDSecureResult: 'error',
          ...(isVendorLoadError && { threeDSecureVendorLoadError: 'true' }),
        },
      });
    }
    setError(e.message);
    setThreeDSecurePending(false);
    setThreeDSecureActionToken(null);
    setParamsForResubmission(null);
  }, []);

  return {
    submitCheckout,
    submitting,
    error,
    threeDSecureActionToken,
    threeDSecurePending,
    onThreeDSecureToken,
    onThreeDSecureError,
  };
}
